<!-- job-details.component.html -->

<div class="jd">

  <!-- ── Page Header ─────────────────────────────── -->
  <div class="jd__header">
    <div class="jd__header-left">
      <a routerLink="/jobs" class="jd__back-btn">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M19 12H5M5 12L12 19M5 12L12 5"
                stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        All Jobs
      </a>
      <div class="jd__header-divider"></div>
      <div>
        <h1 class="jd__title">{{ job?.title || 'Loading…' }}</h1>
        <div class="jd__title-meta" *ngIf="job">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <path d="M12 2C8.1 2 5 5.1 5 9C5 14.2 12 22 12 22S19 14.2 19 9C19 5.1 15.9 2 12 2Z"
                  stroke="currentColor" stroke-width="1.8"/>
            <circle cx="12" cy="9" r="2.5" stroke="currentColor" stroke-width="1.8"/>
          </svg>
          {{ job.locationName }}
        </div>
      </div>
    </div>

    <div class="jd__header-right">
      <!-- Current status badge -->
      <span class="jd__status-badge"
            [class.jd__status-badge--pending]="job?.status === JobStatus.Pending"
            [class.jd__status-badge--progress]="job?.status === JobStatus.InProgress"
            [class.jd__status-badge--complete]="job?.status === JobStatus.Completed"
            [class.jd__status-badge--failed]="job?.status === JobStatus.Failed"
            *ngIf="job">
        <span class="jd__status-dot"></span>
        {{ getStatusLabel(job.status) }}
      </span>

      <!-- View full logs -->
      <a *ngIf="job"
         [routerLink]="['/jobs', job.id, 'logs']"
         class="jd__logs-btn">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
                stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
          <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
          <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        </svg>
        Full Logs
      </a>
    </div>
  </div>

  <!-- ── Loading skeleton ────────────────────────── -->
  <div class="jd__skeleton" *ngIf="loading">
    <div class="jd__skeleton-card"></div>
    <div class="jd__skeleton-card jd__skeleton-card--tall"></div>
  </div>

  <!-- ── Body Grid ────────────────────────────────── -->
  <div class="jd__body" *ngIf="!loading">

    <!-- ══ LEFT COLUMN ═════════════════════════════ -->
    <div class="jd__left">

      <!-- Job Details card -->
      <div class="jd__card">
        <div class="jd__card-head">
          <div class="jd__card-icon jd__card-icon--navy">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.8"/>
              <path d="M16 7V5C16 3.9 15.1 3 14 3H10C8.9 3 8 3.9 8 5V7"
                    stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
          </div>
          <h3 class="jd__card-title">Job Details</h3>
        </div>

        <div class="jd__detail-list">
          <div class="jd__detail-row">
            <span class="jd__detail-label">Location</span>
            <span class="jd__detail-val">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" style="color:var(--fo-muted);margin-right:4px">
                <path d="M12 2C8.1 2 5 5.1 5 9C5 14.2 12 22 12 22S19 14.2 19 9C19 5.1 15.9 2 12 2Z"
                      stroke="currentColor" stroke-width="1.8"/>
                <circle cx="12" cy="9" r="2.5" stroke="currentColor" stroke-width="1.8"/>
              </svg>
              {{ job?.locationName }}
            </span>
          </div>
          <div class="jd__detail-row">
            <span class="jd__detail-label">Assigned To</span>
            <div class="jd__assigned-cell">
              <div class="jd__assigned-avatar" *ngIf="job?.assignedUserId">
                {{ getInitials(getWorkerName(job?.assignedUserId)) }}
              </div>
              <span class="jd__detail-val">{{ getWorkerName(job?.assignedUserId) }}</span>
            </div>
          </div>
          <div class="jd__detail-row">
            <span class="jd__detail-label">Total Logs</span>
            <span class="jd__detail-val">
              <span class="jd__pill jd__pill--teal">{{ logs.length }}</span>
            </span>
          </div>
          <div class="jd__detail-row">
            <span class="jd__detail-label">Hours Logged</span>
            <span class="jd__detail-val">
              <span class="jd__pill jd__pill--navy">{{ totalHours }}h</span>
            </span>
          </div>
        </div>
      </div>

      <!-- Assign Worker card -->
      <div class="jd__card">
        <div class="jd__card-head">
          <div class="jd__card-icon jd__card-icon--teal">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="1.8"/>
              <path d="M2 20C2 17 5 14 9 14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              <line x1="19" y1="8" x2="19" y2="14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              <line x1="16" y1="11" x2="22" y2="11" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
          </div>
          <h3 class="jd__card-title">Assign Worker</h3>
        </div>

        <div class="jd__card-body">
          <div class="jd__select-wrap">
            <svg class="jd__select-icon" width="14" height="14" viewBox="0 0 24 24" fill="none">
              <circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="1.8"/>
              <path d="M2 20C2 17 5 14 9 14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              <circle cx="17" cy="10" r="3" stroke="currentColor" stroke-width="1.8"/>
            </svg>
            <select class="jd__select" [(ngModel)]="selectedWorkerId">
              <option value="">Select a worker…</option>
              <option *ngFor="let w of workers" [value]="w.id">
                {{ w.fullName }}
              </option>
            </select>
          </div>

          <button class="jd__assign-btn"
                  (click)="assignWorker()"
                  [disabled]="!selectedWorkerId || assigning">
            <span class="jd__btn-spinner" *ngIf="assigning"></span>
            <svg *ngIf="!assigning" width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            {{ assigning ? 'Assigning…' : 'Assign Worker' }}
          </button>

          <!-- Success feedback -->
          <div class="jd__assign-success" *ngIf="assignSuccess">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.2"
                    stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Worker assigned successfully
          </div>
        </div>
      </div>

      <!-- Update Status card -->
      <div class="jd__card">
        <div class="jd__card-head">
          <div class="jd__card-icon jd__card-icon--warning">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
              <polyline points="12 6 12 12 16 14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
          </div>
          <h3 class="jd__card-title">Update Status</h3>
        </div>

        <div class="jd__card-body">
          <div class="jd__status-btns">

            <button class="jd__status-btn jd__status-btn--progress"
                    (click)="updateStatus(JobStatus.InProgress)"
                    [class.jd__status-btn--current]="job?.status === JobStatus.InProgress"
                    [disabled]="job?.status === JobStatus.InProgress">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
                <polyline points="12 6 12 12 16 14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              </svg>
              In Progress
            </button>

            <button class="jd__status-btn jd__status-btn--complete"
                    (click)="updateStatus(JobStatus.Completed)"
                    [class.jd__status-btn--current]="job?.status === JobStatus.Completed"
                    [disabled]="job?.status === JobStatus.Completed">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2"
                      stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Completed
            </button>

            <button class="jd__status-btn jd__status-btn--failed"
                    (click)="updateStatus(JobStatus.Failed)"
                    [class.jd__status-btn--current]="job?.status === JobStatus.Failed"
                    [disabled]="job?.status === JobStatus.Failed">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              Failed
            </button>

          </div>
        </div>
      </div>

    </div>

    <!-- ══ RIGHT COLUMN ════════════════════════════ -->
    <div class="jd__right">

      <!-- Map card -->
      <div class="jd__card jd__card--map">
        <div class="jd__card-head">
          <div class="jd__card-icon jd__card-icon--success">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path d="M12 2C8.1 2 5 5.1 5 9C5 14.2 12 22 12 22S19 14.2 19 9C19 5.1 15.9 2 12 2Z"
                    stroke="currentColor" stroke-width="1.8"/>
              <circle cx="12" cy="9" r="2.5" stroke="currentColor" stroke-width="1.8"/>
            </svg>
          </div>
          <h3 class="jd__card-title">GPS Route</h3>
        <span class="jd__card-sub" *ngIf="gpsPointCount > 0">
  {{ gpsPointCount }} {{ gpsPointCount === 1 ? 'waypoint' : 'waypoints' }}
</span>
<span class="jd__card-sub jd__card-sub--empty" *ngIf="gpsPointCount === 0">No GPS data yet</span>
        </div>
             <div id="jobMap" class="jd__map"></div>
        <div class="jd__map-note" *ngIf="hasSinglePointOnly" style="padding:10px 16px; font-size:12px; color:var(--fo-muted); border-top:1px solid var(--fo-border);">
          Only one GPS point has been logged for this job — a route line will appear once a second point is recorded.
        </div>
      </div>
      <!-- Recent Logs card — preview of the 5 most recent, with approve/reject -->
      <div class="jd__card">
        <div class="jd__card-head">
          <div class="jd__card-icon jd__card-icon--info">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
                    stroke="currentColor" stroke-width="1.8"/>
              <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
          </div>
          <h3 class="jd__card-title">Recent Logs</h3>
          <span class="jd__panel-count" *ngIf="logs.length > 0">{{ logs.length }}</span>
          <a *ngIf="job" [routerLink]="['/jobs', job.id, 'logs']" class="jd__view-all-link">
            View all
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
              <path d="M5 12H19M13 6L19 12L13 18" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </a>
        </div>

        <!-- Empty logs -->
        <div class="jd__logs-empty" *ngIf="logs.length === 0">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
            <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
                  stroke="currentColor" stroke-width="1.4"/>
          </svg>
          <p>No logs submitted yet.</p>
        </div>

        <!-- Log cards — showing recent 5, approve / reject workflow -->
        <div class="jd__log-cards" *ngIf="logs.length > 0">
          <div class="log-card"
               *ngFor="let log of recentLogs"
               [class.log-card--approved]="log.approvalStatus === 'Approved'"
               [class.log-card--rejected]="log.approvalStatus === 'Rejected'">

            <!-- Log header: worker name + approval badge -->
            <div class="log-card__header">
              <div class="log-card__worker">
                <span class="log-card__avatar">
                  {{ (log.userName || getWorkerName(log.userId))?.charAt(0)?.toUpperCase() }}
                </span>
                <div>
                  <span class="log-card__name">{{ log.userName || getWorkerName(log.userId) }}</span>
                  <span class="log-card__date">
                    {{ log.createdUtc | date:'dd MMM yyyy, HH:mm' }}
                  </span>
                </div>
              </div>

              <!-- Approval status badge -->
              <span class="log-card__badge log-card__badge--pending"
                    *ngIf="log.approvalStatus === 'Pending'">
                ⏳ Pending
              </span>
              <span class="log-card__badge log-card__badge--approved"
                    *ngIf="log.approvalStatus === 'Approved'">
                ✓ Approved
              </span>
              <span class="log-card__badge log-card__badge--rejected"
                    *ngIf="log.approvalStatus === 'Rejected'">
                ✗ Rejected
              </span>
            </div>

            <!-- Work description -->
            <p class="log-card__work">{{ log.workDone || log.notes }}</p>

            <!-- ✅ NEW — Photo thumbnails, so the manager can visually
                 confirm the work before approving, not just read text. -->
            <div class="log-card__photos" *ngIf="log.photos && log.photos.length > 0">
              <a *ngFor="let photo of log.photos"
                 [href]="photo.url"
                 target="_blank"
                 rel="noopener"
                 class="log-card__photo-link">
                <img [src]="photo.url" class="log-card__photo" alt="Work log photo"/>
              </a>
            </div>

            <!-- Hours -->
            <div class="log-card__meta">
              <span class="log-card__hours">⏱ {{ log.hoursSpent }}h logged</span>
              <span class="log-card__location" *ngIf="log.latitude">
                📍 GPS captured
              </span>
            </div>

            <!-- Rejection reason -->
            <div class="log-card__rejection"
                 *ngIf="log.approvalStatus === 'Rejected' && log.approvalComment">
              <span class="log-card__rejection-label">Rejected by {{ log.approvedByName }}:</span>
              <span class="log-card__rejection-reason">{{ log.approvalComment }}</span>
            </div>

            <!-- Approved by -->
            <div class="log-card__approved-by"
                 *ngIf="log.approvalStatus === 'Approved'">
              ✓ Approved by {{ log.approvedByName }}
            </div>

            <!-- Approve / Reject buttons — only shown for Pending logs -->
            <div class="log-card__actions"
                 *ngIf="log.approvalStatus === 'Pending'">
              <button class="log-card__approve-btn"
                      (click)="approveLog(log.id)">
                ✓ Approve
              </button>
              <button class="log-card__reject-btn"
                      (click)="rejectLog(log.id)">
                ✗ Reject
              </button>
            </div>

          </div>
        </div>

      </div>

    </div>

  </div>

</div>